import React, { useEffect, useState } from 'react';
import { collection, getCountFromServer, query, where, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import firebase from '../db/firebase';
import {
  Users2,
  PhoneCall,
  HandCoins,
  Package,
  NotebookPenIcon,
} from 'lucide-react';
import IconCoin from '../assets/icons/white-coin.svg';
import { motion } from 'framer-motion';
import { useSpring, animated } from '@react-spring/web';
import Car from '../assets/icons/car2.svg';

const AnimatedNumber = ({ number }) => {
  const { number: animatedNumber } = useSpring({
    from: { number: 0 },
    to: { number },
    config: { duration: 800 },
  });

  return (
    <animated.span className="text-3xl font-bold text-blue-600 mt-1 block">
      {animatedNumber.to(n => Math.floor(n))}
    </animated.span>
  );
};

const Home = () => {
  const [counts, setCounts] = useState({
    users: 0,
    requests: 0,
    remoteSurveys: 0,
    requestsMoney: 0,
    requestsProduct: 0,
    purchasePoints: 0,
  });

  const navigate = useNavigate();

  // Total users
  useEffect(() => {
    const fetchUsersCount = async () => {
      try {
        const usersSnap = await getCountFromServer(
          collection(firebase.db, 'userData')
        );
        setCounts(prev => ({ ...prev, users: usersSnap.data().count }));
      } catch (error) {
        console.error('Error fetching users count:', error);
      }
    };

    fetchUsersCount();
  }, []);

  // Realtime listeners for pending items
  useEffect(() => {
    const configs = [
      { key: 'requests', col: 'requests' },
      { key: 'remoteSurveys', col: 'remoteSurveys' },
      { key: 'requestsMoney', col: 'requestsMoney' },
      { key: 'requestsProduct', col: 'requestsProduct' },
      { key: 'purchasePoints', col: 'requestPoint' },
    ];

    const unsubscribes = configs.map(({ key, col }) => {
      const q = query(
        collection(firebase.db, col),
        where('state', '==', 'pending')
      );

      return onSnapshot(
        q,
        snap => {
          setCounts(prev => ({ ...prev, [key]: snap.size }));
        },
        err => console.error(`Realtime error for ${col}:`, err)
      );
    });

    return () => unsubscribes.forEach(u => u && u());
  }, []);

  const cards = [
    { label: 'Users', count: counts.users, icon: Users2, path: '/users' },
    { label: 'Request a Call', count: counts.requests, icon: PhoneCall, path: '/requests' },
    { label: 'Remote Surveys', count: counts.remoteSurveys, icon: NotebookPenIcon, path: '/remote-surveys' },
    { label: 'Purchase Points', count: counts.purchasePoints, icon: IconCoin, path: '/purchase-points' },
    { label: 'Request Money', count: counts.requestsMoney, icon: HandCoins, path: '/requests-money' },
    { label: 'Request Products', count: counts.requestsProduct, icon: Package, path: '/requests-products' },
  ];

  return (
    <div className="bg-gray-300 flex-1 flex items-center justify-center p-6 h-[100vh] overflow-hidden relative">
      {/* Main content */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-7xl z-10"
      >
        <h1 className="text-3xl font-bold mb-8 text-gray-800 text-center">
          Dashboard Summary
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map(({ label, count, icon, path }, index) => {
            const isImage = typeof icon === 'string';

            return (
              <motion.div
                key={label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.05 * index, duration: 0.35 }}
                onClick={() => navigate(path)}
                className="bg-blue-50 border border-blue-100 p-5 rounded-lg shadow-md flex items-center justify-between hover:shadow-lg transition cursor-pointer"
              >
                <div>
                  <h2 className="text-lg font-semibold text-gray-700">
                    {label}
                  </h2>
                  <AnimatedNumber number={count} />
                </div>

                {isImage ? (
                  <img
                    src={icon}
                    alt={`${label} icon`}
                    className="h-9 w-9 p-1.5 bg-white border border-gray-200 shadow-md rounded-full"
                  />
                ) : (
                  React.createElement(icon, {
                    size: 36,
                    className: 'text-blue-500',
                  })
                )}
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Maintenance notice – top right */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.35 }}
        className="absolute top-4 right-4 z-20  max-w-xl"
      >
        <div className="bg-white/80 backdrop-blur-sm border border-gray-300 rounded-md px-4 py-3">
          <p className="text-xs text-red-700 font-medium leading-relaxed">
            No maintenance service since{' '}
            <span className="underline">13 February 2026</span>
          </p>

          <a
            href="/McNallyEV_Maintenance_Cancellation_Notice.pdf"
            download
            className="mt-1 inline-block text-[11px] text-gray-700 underline hover:text-gray-900"
          >
            Download notice
          </a>
        </div>
      </motion.div>

      {/* Decorative car icon */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        className="absolute bottom-[-50px] right-[-25px] z-0"
      >
        <img
          src={Car}
          alt="Car Icon"
          className="h-[250px] w-[250px] sm:h-[300px] sm:w-[300px] object-contain pointer-events-none"
        />
      </motion.div>
    </div>
  );
};

export default Home;